import { Component, OnInit } from '@angular/core';
import { FieldType, FieldTypeConfig } from '@ngx-formly/core';

import { TranslationLanguage } from '../../translation/models/translation-language.interface';

/**
 * Formly wrapper around NoteTranslationComponent.
 * Bridges the component's notesChanged/notesLanguageChanged outputs to the
 * formly field's formControl so the translated notes are included in the
 * submitted model.
 *
 * The field value is stored as a JSON string matching the format used by the
 * tripetto note-translation component:
 *   { "notes": "...", "translationLanguageCode": "..." }
 *
 * Props:
 *   label            — textarea label (default: 'Notes')
 *   maxCharLength    — max character limit (default: 8000)
 *   isEncounterNotes — true to link translation requests to the current encounter
 *
 * The host component should set formState.noteTranslationPreloadText to the
 * existing encounter notes so the textarea is pre-populated on load.
 */
@Component({
	selector: 'app-mat-formly-note-translation',
	templateUrl: './mat-formly-note-translation.component.html',
	styleUrls: ['./mat-formly-note-translation.component.scss'],
	standalone: false
})
export class MatFormlyNoteTranslationComponent extends FieldType<FieldTypeConfig> implements OnInit {
	public preloadedText = '';
	public preloadedLanguageCode = '';

	private currentNotes = '';
	private currentLanguageCode = '';

	ngOnInit(): void {
		const existingValue = this.formControl.value as string | undefined;
		if (existingValue) {
			try {
				const parsed = JSON.parse(existingValue) as { notes?: string; translationLanguageCode?: string };
				this.preloadedText = parsed.notes ?? '';
				this.preloadedLanguageCode = parsed.translationLanguageCode ?? '';
			} catch {
				// Plain string value — treat it as preloaded notes text
				this.preloadedText = existingValue;
			}
			this.currentNotes = this.preloadedText;
			this.currentLanguageCode = this.preloadedLanguageCode;
		} else {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			this.preloadedText = (this.options as any)?.formState?.noteTranslationPreloadText ?? '';
			this.currentNotes = this.preloadedText;
		}
	}

	public onNotesChanged(event: { target: { value: string } }): void {
		this.currentNotes = event.target.value;
		this.updateFormControl();
	}

	public onLanguageChanged(language: TranslationLanguage): void {
		this.currentLanguageCode = language.translationLanguageCode;
		this.updateFormControl();
	}

	private updateFormControl(): void {
		this.formControl.setValue(
			JSON.stringify({
				notes: this.currentNotes,
				translationLanguageCode: this.currentLanguageCode
			})
		);
		this.formControl.markAsDirty();
	}
}
